import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Row } from 'reactstrap';
import { pdf } from '@react-pdf/renderer';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormInput from '../../components/form/FormInput';
import { productTypeOptions } from '../../utils/common';
import CallexLoader from '../../components/CallexLoader';
import FormSelect from '../../components/form/FormSelect';
import AdminHeader from '../../components/header/AdminHeader';
import ManualOrderPDF from '../../components/ManualOrderPDF';

import {
  areaOptionsGet,
  beatOptionsGetById,
  crmProductListGet,
  outletOptionsGetById,
  productSave
} from '../../store/slices/tenantSlices/productSlice';
import { useUserDetails } from '../../store/slices/authSlice';
import SaveConfirmationModal from '../../components/Modals/SaveConfirmationModal';
import { useTranslation } from 'react-i18next';

const CrmProduct = () => {
  const dispatch = useDispatch();
  const user = useUserDetails();
  const { t } = useTranslation();

  const [areaOption, setAreaOption] = useState([]);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [beatOption, setBeatOption] = useState([]);
  const [outletOption, setOutletOption] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [productList, setProductList] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [count, setCount] = useState([]);
  const [filter, setFilter] = useState({
    search: ''
  });

  const initialValues = {
    area_id: '',
    beat_id: '',
    outlet_id: '',
    total_amount: 0,
    total_quantity: 0,
    product_details: []
  };

  const validationSchema = Yup.object({
    outlet_id: Yup.string().required('Outlet is required'),
    area_id: Yup.string().required('Area is required'),
    beat_id: Yup.string().required('Beat is required')
  });

  const onSubmit = () => {
    setOpenConfirmationModal(true);
  };

  const onSaveProduct = async () => {
    try {
      const productDetails = productList
        .filter((product) => product?.quantity > 0)
        .map((product) => ({
          product_id: product.id,
          product_name: product.name,
          uom: product.uom,
          product_type: product.product_type,
          rate: product.rate,
          quantity: product.quantity,
          total_amount: product.total_amount
        }));

      const payload = { ...values, product_details: productDetails };
      const response = await dispatch(productSave(payload)).unwrap();
      const areaLabel = areaOption?.find((option) => option.value === values.area_id)?.label;
      const beatLabel = beatOption?.find((option) => option.value === values.beat_id)?.label;
      const outletLabel = outletOption?.find((option) => option.value === values.outlet_id)?.label;
      const pdfData = {
        ...payload,
        area: areaLabel,
        beat: beatLabel,
        outlet: outletLabel,
        username: user.username
      };
      // Generate the PDF and trigger the download
      const pdfBlob = await pdf(<ManualOrderPDF data={pdfData} />).toBlob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(pdfBlob);
      link.download = 'manual_order.pdf';
      link.click();
      toast.success(response?.message);
      resetForm();
      fetchProductList();
    } catch (error) {
      console.error('Error during submission:', error);
    }
  };

  const { values, handleChange, handleBlur, handleSubmit, touched, errors, setValues, resetForm } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });

  const getAreaOption = async () => {
    try {
      const response = await dispatch(areaOptionsGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setAreaOption(options);
      } else {
        setAreaOption([]);
      }
    } catch (error) {
      setAreaOption([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getAreaOption();
  }, []);

  const getBeatOption = async () => {
    try {
      const response = await dispatch(beatOptionsGetById({ area_id: values.area_id })).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setBeatOption(options);
      } else {
        setBeatOption([]);
      }
    } catch (error) {
      setBeatOption([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (values.area_id) {
      getBeatOption();
    } else {
      setBeatOption([]);
    }
  }, [values.area_id]);

  const getOutletOption = async () => {
    try {
      const response = await dispatch(outletOptionsGetById({ beat_id: values.beat_id })).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setOutletOption(options);
      } else {
        setOutletOption([]);
      }
    } catch (error) {
      setOutletOption([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (values.beat_id) {
      getOutletOption();
    } else {
      setOutletOption([]);
    }
  }, [values.beat_id]);

  const fetchProductList = async () => {
    setIsLoading(true);
    try {
      const payload = { ...filter };
      const response = await dispatch(crmProductListGet(payload)).unwrap();
      const newData = response?.data?.map((x) => ({ ...x, quantity: 0, total_amount: 0 }));
      setProductList(newData || []);
      setIsLoading(false);
      setOpenConfirmationModal(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  useEffect(() => {
    fetchProductList();
  }, []);

  useEffect(() => {
    if (productList?.length > 0) {
      const totalAmount = productList.reduce((acc, x) => acc + (x.total_amount || 0), 0);
      const totalQuantity = productList.reduce((acc, x) => acc + (x.quantity || 0), 0);

      setValues({
        ...values,
        total_amount: totalAmount,
        total_quantity: totalQuantity
      });
    }
  }, [productList]);

  useEffect(() => {
    if (productList?.length > 0) {
      const filteredProducts = productList.filter(
        (product) =>
          (activeTab === 'all' ? true : product?.product_type === activeTab) &&
          product?.name?.toLowerCase()?.includes(filter?.search?.toLowerCase())
      );

      const productTypeCount = filteredProducts.reduce((acc, product) => {
        const type = product.product_type;
        if (acc[type]) {
          acc[type] += 1;
        } else {
          acc[type] = 1;
        }
        return acc;
      }, {});

      setCount({ all: filteredProducts?.length, ...productTypeCount });
    }
  }, [filter, productList]);

  return (
    <React.Fragment>
      <AdminHeader
        isExpandSearch
        search={filter.search}
        title={t('manualOrder')}
        searchPlaceholder="Product Search"
        handelSearch={(e) => setFilter({ ...filter, search: e.target.value })}
      />
      <Card className="m-0 px-4 pb-2 pt-3">
        <form onSubmit={handleSubmit} id="crm-product">
          <Row>
            <Col lg="3" md="3" className="flex-row">
              <FormSelect
                label={t('area')}
                placeholder="Select Area"
                value={values.area_id}
                onChange={handleChange}
                onBlur={handleBlur}
                name="area_id"
                options={areaOption}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3" md="3" className="flex-row">
              <FormSelect
                label={t('beat')}
                placeholder="Select Beat"
                value={values.beat_id}
                onChange={handleChange}
                onBlur={handleBlur}
                name="beat_id"
                options={beatOption}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3" md="3" className="flex-row">
              <FormSelect
                label={t('outlet')}
                placeholder="Select Outlet"
                value={values.outlet_id}
                onChange={handleChange}
                onBlur={handleBlur}
                name="outlet_id"
                options={outletOption}
                touched={touched}
                errors={errors}
              />
            </Col>
          </Row>
        </form>
      </Card>
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 235px)' }}>
          <CallexLoader />
        </Card>
      ) : productList?.length > 0 ? (
        <React.Fragment>
          <Card className="d-flex flex-row mt-3 pt-2 mb-0 rounded-bottom-0 overflow-y-auto">
            {[{ label: 'All', value: 'all' }, ...productTypeOptions].map((val, index) => {
              return (
                <div
                  key={index}
                  className={`px-3 py-2 cursor-pointer fw-bolder ${val?.value === activeTab ? 'text-info' : ''}`}
                  style={{ textWrap: 'nowrap' }}
                  onClick={() => setActiveTab(val?.value)}>
                  {val?.label}&nbsp;{'(' + (count[val?.value] ? count[val?.value] : 0) + ')'}
                </div>
              );
            })}
          </Card>
          <Card className="rounded-top-0 pt-2 position-relative m-0">
            <div className="daily-report-table" style={{ height: 'calc(100vh - 410px)' }}>
              <table className="report-list-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>UOM</th>
                    <th className="text-center" style={{ width: '120px' }}>
                      Rate
                    </th>
                    <th className="text-center" style={{ width: '120px' }}>
                      Quantity
                    </th>
                    <th className="text-center" style={{ width: '120px' }}>
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {productList
                    ?.filter(
                      (x) =>
                        (activeTab === 'all' ? true : x.product_type === activeTab) &&
                        x.name.toLowerCase().includes(filter?.search?.toLowerCase())
                    )
                    ?.map((val, index) => {
                      return (
                        <tr key={index}>
                          <td>{val?.name}</td>
                          <td>{val?.uom}</td>
                          <td className="text-end">{val?.rate}</td>
                          <td className="text-end p-0">
                            <FormInput
                              className="pb-0 select-mb-0"
                              inputClassName="table-input text-end"
                              isShowLabel={false}
                              value={val?.quantity}
                              onChange={(e) => {
                                const newQuantity = e.target.value;
                                const validQuantity =
                                  newQuantity.trim() === '' ? 0 : parseFloat(newQuantity);
                                if (!isNaN(validQuantity)) {
                                  const updatedProductList = productList.map((product) =>
                                    product.id === val?.id
                                      ? {
                                          ...product,
                                          quantity: parseFloat(newQuantity),
                                          total_amount: parseFloat(newQuantity) * product?.rate
                                        }
                                      : product
                                  );
                                  setProductList(updatedProductList);
                                }
                              }}
                            />
                          </td>
                          <td className="text-end">{val?.total_amount.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
            <Row className="py-4 align-items-center">
              <Col lg="4" md="4">
                <label className="text-info fw-bolder fs-5">Total Amount :&nbsp;</label>
                <label className="text-primary fw-bolder fs-5">
                  {values?.total_amount?.toFixed(2)}
                </label>
              </Col>
              <Col lg="4" md="4">
                <label className="text-info fw-bolder fs-5">Total Quantity :&nbsp;</label>
                <label className="text-primary fw-bolder fs-5">{values?.total_quantity}</label>
              </Col>
              <Col lg="4" md="4">
                <Button color="primary" className="px-5" type="submit" form="crm-product">
                  {t('save')}
                </Button>
              </Col>
            </Row>
          </Card>
        </React.Fragment>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: 'calc(100vh - 235px)' }}>
          {t('noDataAvailable')}
        </Card>
      )}
      <SaveConfirmationModal
        isOpen={openConfirmationModal}
        onCloseClick={() => setOpenConfirmationModal(false)}
        onSaveClick={() => onSaveProduct()}
      />
    </React.Fragment>
  );
};

export default CrmProduct;
